import { Button, Card, Expandable, Text } from '@components/ui';
import type { JobData } from '@features/jobs/jobData';
import { mobileAppStoreUrl } from '@lib/appStoreUrl';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import { useTranslation } from 'react-i18next';

interface JobActionsProps {
  job: JobData;
  openAlert: boolean;
  loading: boolean;
  onCloseAlert: () => void;
  onBeginRating: () => void;
  onAccept: () => void;
}

const JobActions = ({
  job,
  openAlert,
  loading,
  onCloseAlert,
  onBeginRating,
  onAccept,
}: JobActionsProps): JSX.Element => {
  const { t } = useTranslation();

  if (job.status !== 'Accepted') {
    return (
      <Button
        variant="primary"
        fullWidth
        sx={{ mt: 3 }}
        onClick={onAccept}
        loading={loading}
      >
        {t('jobDetail.acceptJob')}
      </Button>
    );
  }

  if (!openAlert) {
    return (
      <Button
        variant="primary"
        fullWidth
        sx={{ mt: 3 }}
        onClick={onBeginRating}
      >
        {job.ratingStartedAt != null
          ? t('jobDetail.continueRating')
          : t('jobDetail.beginRating')}
      </Button>
    );
  }

  return (
    <Expandable open={openAlert}>
      <Card
        sx={{
          position: 'relative',
          mt: 3,
          borderRadius: '20px',
          p: '20px 48px 20px 20px',
        }}
      >
        <IconButton
          aria-label={t('common.close')}
          onClick={onCloseAlert}
          sx={{
            position: 'absolute',
            right: 12,
            top: 12,
            height: 32,
            width: 32,
            color: 'text.secondary',
            '&:hover': {
              bgcolor: 'rgb(var(--ink) / 0.1)',
              color: 'text.primary',
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <PhoneIphoneIcon
            sx={{ flexShrink: 0, fontSize: 28, color: 'primary.main' }}
          />
          <Box>
            <Text variant="body" tone="muted" sx={{ mb: '10px' }}>
              {t('jobDetail.completeInApp')}
            </Text>
            <Link
              href={mobileAppStoreUrl()}
              underline="hover"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                fontWeight: 700,
              }}
            >
              <Text as="span" variant="label" tone="primary">
                {t('jobDetail.downloadApp')}
              </Text>
            </Link>
          </Box>
        </Box>
      </Card>
    </Expandable>
  );
};

export default JobActions;
